// exerciseVisual.tsx — per-muscle-group color + icon identity, so an exercise reads as itself at a
// glance instead of every row looking like every other row (the flat, one-accent-color look). No new
// asset pipeline: a distinct hue + a lucide icon per of the 8 muscleGroup values in exercises.ts.
import { Dumbbell, Activity, Footprints, Zap, Flame, Waves, HeartPulse, PersonStanding, type LucideIcon } from "lucide-react";
import { presetExercises, type PresetExercise } from "./exercises";

export const MUSCLE_COLOR: Record<PresetExercise["muscleGroup"], string> = {
  chest: "#ea580c",
  back: "#2563eb",
  legs: "#0d9488",
  shoulders: "#7c3aed",
  arms: "#db2777",
  core: "#a16207",
  cardio: "#0891b2",
  full: "#64748b",
};

export const MUSCLE_ICON: Record<PresetExercise["muscleGroup"], LucideIcon> = {
  chest: Dumbbell,
  back: Activity,
  legs: Footprints,
  shoulders: Zap,
  arms: Flame,
  core: Waves,
  cardio: HeartPulse,
  full: PersonStanding,
};

// LiftSignal/PR rows carry a display NAME, not a slug — resolve by name (falls back to a neutral
// "full body" identity for an AI-drafted exercise our own name lookup can't place, same honest-
// degradation posture as everything else that touches the AI-drafted shape).
const byName = new Map(presetExercises.map((p) => [p.name, p]));
export function exerciseVisual(name: string): { color: string; Icon: LucideIcon } {
  const group = byName.get(name)?.muscleGroup ?? "full";
  return { color: MUSCLE_COLOR[group], Icon: MUSCLE_ICON[group] };
}

export function ExerciseIcon({ name, size = 15 }: { name: string; size?: number }) {
  const { color, Icon } = exerciseVisual(name);
  return (
    <span
      className="row"
      style={{
        width: size + 10, height: size + 10, borderRadius: 999, flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: `color-mix(in srgb, ${color} 16%, transparent)`, color,
      }}
    >
      <Icon size={size} />
    </span>
  );
}
